/**
 * ADR-0506: Structured Error Logging
 *
 * Thin wrapper that:
 *  - In development: writes to console.warn with full error object
 *  - In production: fire-and-forgets a POST /api/client-error request
 *
 * Usage:
 *   import { logger } from "../logger";
 *   // or via window global: window.WMULogger.warn("tag", err, { ctx })
 */

type LogCtx = Record<string, unknown>;

export type WMULoggerAPI = {
  warn(tag: string, err: unknown, ctx?: LogCtx): void;
  info(tag: string, message: string, ctx?: LogCtx): void;
};

const isDev =
  location.hostname === "localhost" ||
  location.hostname === "127.0.0.1" ||
  location.hostname.endsWith(".local");

function serialize(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function send(payload: Record<string, unknown>): void {
  try {
    fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* never throw — logger must be silent */
    });
  } catch {
    /* never throw */
  }
}

const api: WMULoggerAPI = {
  warn(tag: string, err: unknown, ctx?: LogCtx): void {
    if (isDev) {
      console.warn(`[WMU:${tag}]`, err, ctx ?? "");
      return;
    }
    send({ level: "warn", tag, message: serialize(err), ctx: ctx ?? null });
  },

  info(tag: string, message: string, ctx?: LogCtx): void {
    if (isDev) {
      console.info(`[WMU:${tag}] ${message}`, ctx ?? "");
      return;
    }
    send({ level: "info", tag, message, ctx: ctx ?? null });
  },
};

window.WMULogger = api;

/** Named export so TS modules can import directly without accessing window. */
export const logger: WMULoggerAPI = api;
